import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Plus, Pencil, Trash2, User, Shield, Key, Eye, EyeOff } from 'lucide-react';

const UserManagement = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user'
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch users:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const url = editingUser 
      ? `${API_URL}/api/admin/users/${editingUser.id}`
      : `${API_URL}/api/admin/users`;
    
    const method = editingUser ? 'PUT' : 'POST';
    
    const body = editingUser 
      ? { username: formData.username, email: formData.email, role: formData.role }
      : formData;

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        fetchUsers();
        closeModal();
      } else {
        const error = await response.json();
        alert(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Failed to save user:', error);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUserId}/reset-password?new_password=${encodeURIComponent(newPassword)}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setShowPasswordModal(false);
        setNewPassword('');
        setSelectedUserId(null);
        alert('Passwort erfolgreich geändert');
      } else {
        const error = await response.json();
        alert(error.detail || 'Fehler beim Ändern des Passworts');
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ username: '', email: '', password: '', role: 'user' });
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#1B4D3E' }}>Benutzer</h1>
          <p className="text-gray-500">Benutzerverwaltung und Zugriffsrechte</p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          style={{ backgroundColor: '#1B4D3E' }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Benutzer
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="divide-y">
          {users.map((user) => (
            <div key={user.id} className="flex items-center gap-4 p-4 hover:bg-gray-50">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
                style={{ backgroundColor: user.role === 'admin' ? '#1B4D3E' : '#6B7280' }}
              >
                {user.username.charAt(0).toUpperCase()}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{user.username}</h3>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                    user.role === 'admin' 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    <Shield className="w-3 h-3" />
                    {user.role === 'admin' ? 'Admin' : 'Benutzer'}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setSelectedUserId(user.id);
                    setShowPasswordModal(true);
                  }}
                  className="p-2 hover:bg-gray-200 rounded-lg"
                  title="Passwort ändern"
                >
                  <Key className="w-4 h-4 text-gray-500" />
                </button>
                <button
                  onClick={() => openEditModal(user)}
                  className="p-2 hover:bg-gray-200 rounded-lg"
                >
                  <Pencil className="w-4 h-4" style={{ color: '#1B4D3E' }} />
                </button>
                {user.id !== currentUser?.sub && (
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="p-2 hover:bg-red-100 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Berechtigungen Info */}
      <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-lg font-semibold mb-4" style={{ color: '#1B4D3E' }}>
          Berechtigungen
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-purple-50">
            <h3 className="font-semibold text-purple-700 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Admin
            </h3>
            <ul className="mt-2 text-sm text-purple-600 space-y-1">
              <li>• Mitarbeiter verwalten</li>
              <li>• Behandlungsfelder bearbeiten</li>
              <li>• Popup-Fenster verwalten</li>
              <li>• Kontaktdaten ändern</li>
              <li>• Benutzer verwalten</li>
              <li>• E-Mail Einstellungen</li>
            </ul>
          </div>
          <div className="p-4 rounded-lg bg-gray-50">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
              <User className="w-4 h-4" />
              Benutzer
            </h3>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Popup-Fenster verwalten</li>
              <li>• Kontaktdaten ändern</li>
            </ul>
          </div>
        </div>
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#1B4D3E' }}>
              {editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Benutzername</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">E-Mail</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium mb-2">Passwort</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg"
                    required={!editingUser}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Rolle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="user">Benutzer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={closeModal} className="flex-1">
                  Abbrechen
                </Button>
                <Button type="submit" className="flex-1" style={{ backgroundColor: '#1B4D3E' }}>
                  Speichern
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#1B4D3E' }}>
              Passwort ändern
            </h2>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Neues Passwort</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setSelectedUserId(null);
                  }} 
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button type="submit" className="flex-1" style={{ backgroundColor: '#1B4D3E' }}>
                  Passwort ändern
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
